/**
 * Wraps fetch with an AbortController-based timeout.
 * If `signal` is provided in init, it is merged with the timeout signal.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number }
): Promise<Response> {
  const { timeoutMs = 30_000, ...fetchInit } = init ?? {};

  const controller = new AbortController();
  const externalSignal = fetchInit.signal;
  let clearExternalListener: (() => void) | undefined;

  // If caller already provides a signal, abort our controller when that fires
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      const forwardAbort = () => {
        controller.abort(
          externalSignal.reason ?? new DOMException('Request aborted', 'AbortError')
        );
      };
      externalSignal.addEventListener('abort', forwardAbort, { once: true });
      clearExternalListener = () => externalSignal.removeEventListener('abort', forwardAbort);
    }
  }

  const timeoutError = new DOMException('Request timed out', 'TimeoutError');
  const timer = setTimeout(() => controller.abort(timeoutError), timeoutMs);

  try {
    return await fetch(input, { ...fetchInit, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    clearExternalListener?.();
  }
}
